<template>
  <div v-if="!loading" class="report-stage">
    <template v-if="isWordTemplateReportEnabled()">
      <template v-if="reportBuilderType === 'word'">
        <div class="report-stage__word-builder px-view pt-12">
          <div class="flex -mx-4">
            <div class="w-1/3 px-4">
              <ReportBuilderTypeSelector v-model="reportBuilderType" />

              <div class="mt-6">
                <p class="mb-4 text-80">{{ $t('template-builder.report-builder-type.word-instruction') }}</p>
                <WordTemplateUpload
                  :existing-file="existingWordTemplate"
                  @file-change="onWordTemplateFileChange"
                  @file-deleted="onWordTemplateFileDeleted"
                  @uploading-change="onWordTemplateUploadingChange"
                />
              </div>
            </div>

            <div class="w-2/3 px-4 word-report-instructions-page">
              <h3 class="word-report-instructions-page__title">{{ $t('template-builder.word-report.page-title') }}</h3>
              <WordReportInstructions />
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="p-view">
          <div class="report-stage__controls big:py-view">
            <ReportBuilderTypeSelector v-model="reportBuilderType" class="mb-6" />

            <ReportTemplateEditor
              v-if="editing"
              :template="editedReportTemplate"
              :resource-id="resourceId"
              @save="save"
              @discard="discard"
            />

            <ReportTemplateSelector
              v-else
              :report-templates="filteredReportTemplates"
              :selected-report-template="selectedReportTemplate"
              @select="select"
              @edit="edit"
              @add="add"
              @delete="deleteTemplate"
            />
          </div>

          <div class="report-preview-form pt-6">
            <h1 class="report-preview-form__header my-3">{{ $t('Preview example') }}</h1>
            <Preview
              class="card p-6"
              :resource="previewData"
              :template="previewReportTemplate"
            />
          </div>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="p-view">
        <div class="report-stage__controls big:py-view">
          <ReportTemplateEditor
            v-if="editing"
            :template="editedReportTemplate"
            :resource-id="resourceId"
            @save="save"
            @discard="discard"
          />

          <ReportTemplateSelector
            v-else
            :report-templates="filteredReportTemplates"
            :selected-report-template="selectedReportTemplate"
            @select="select"
            @edit="edit"
            @add="add"
            @delete="deleteTemplate"
          />
        </div>

        <div class="report-preview-form pt-6">
          <h1 class="report-preview-form__header my-3">{{ $t('Preview example') }}</h1>
          <Preview
            class="card p-6"
            :resource="previewData"
            :template="previewReportTemplate"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import Preview from '@/_legacy/views/ChecklistBuilder/Report/Preview.vue';
  import ReportTemplateSelector from '@/_legacy/views/ChecklistBuilder/Report/ReportTemplateSelector.vue';
  import ReportTemplateEditor from '@/_legacy/views/ChecklistBuilder/Report/ReportTemplateEditor.vue';
  import ReportBuilderTypeSelector from '@/_legacy/views/ChecklistBuilder/Report/ReportBuilderTypeSelector.vue';
  import WordReportInstructions from '@/_legacy/views/ChecklistBuilder/Report/WordReportInstructions.vue';
  import WordTemplateUpload from '@/ui/components/common/WordTemplateUpload.vue';
  import previewDataEn from '@/_legacy/views/ChecklistBuilder/Report/previews/en.json';
  import previewDataDe from '@/_legacy/views/ChecklistBuilder/Report/previews/de.json';
  import exampleDefaultReportTemplate from '@/_legacy/views/ChecklistBuilder/Report/previews/exampleDefaultReportTemplate.json';
  import { user as authUser } from '@/ui/auth/auth';
  import { useTask } from '@/ui/concerns';
  import { create, list, remove, update, get } from '@/services/api/resources/report-template';
  import {
    upload as uploadWordTemplate,
    update as updateWordTemplate,
    remove as removeWordTemplate,
    getByChecklist,
  } from '@/services/api/resources/checklist-word-template';
  import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
  import { versioned } from '@/services/api';
  import { v4 as uuidv4 } from 'uuid';
  import { isWordTemplateReportEnabled } from '@/ui/helpers/featureHelper';

  export default {
    components: {
      Preview,
      ReportTemplateSelector,
      ReportTemplateEditor,
      ReportBuilderTypeSelector,
      WordReportInstructions,
      WordTemplateUpload,
    },
    props: {
      isUpdate: {
        type: Boolean,
        required: true,
      },
      isCloning: {
        type: Boolean,
        required: true,
      },
      resourceId: {
        type: String,
        default: null,
      },
    },
    data: () => ({
      loading: true,
      reportBuilderType: 'lumiform',
      wordTemplateFile: null,
      existingWordTemplate: null,
    }),
    computed: {
      editing() {
        return this.editedReportTemplate !== null;
      },
      editedReportTemplate() {
        return this.$store.getters['checklist_builder/edited_report_template'];
      },
      checklist() {
        return this.$store.getters['checklist_builder/checklist'];
      },
      reportTemplates() {
        return this.$store.getters['checklist_builder/report_templates'];
      },
      filteredReportTemplates() {
        return this.reportTemplates;
      },
      selectedReportTemplate() {
        if (this.checklist.report_template === null) {
          return this.reportTemplates[0];
        }
        const template = this.reportTemplates
          .find(({ id }) => id === this.checklist.report_template);

        if (template) {
          this.updateAverageScoreOnChecklist(template);
          return template;
        }

        return this.reportTemplates[0]; // default to first one if not found
      },
      previewData() {
        return authUser.value.locale === 'de' ? previewDataDe : previewDataEn;
      },
      defaultReportTemplate() {
        return this.reportTemplates.find(({ isDefault }) => isDefault === 1) || exampleDefaultReportTemplate;
      },
      previewReportTemplate() {
        if (this.editedReportTemplate) {
          return this.editedReportTemplate.data.template;
        }
        return this.selectedReportTemplate.data.template;
      },
    },
    watch: {
      reportBuilderType(newType, oldType) {
        if (oldType && newType !== oldType) {
          if (newType === 'word') {
            this.checklist.report_template = null;
            if (this.existingWordTemplate?.id) {
              this.checklist.wordTemplateId = this.existingWordTemplate.id;
            }
          } else if (newType === 'lumiform') {
            this.checklist.wordTemplateId = null;
          }
        }
      },
    },
    created() {
      this.loading = true;
      this.fetch();
    },
    mounted() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.determineReportBuilderType();
        }, 100);
      });
    },
    methods: {
      isWordTemplateReportEnabled,
      fetch() {
        const getRecord = useTask(
          () => list({ page: 1, perPage: 1000 }),
          ({ data, meta }) => {
            const reportTemplates = [meta.default, ...data];
            this.$store.commit('checklist_builder/loadReportTemplates', reportTemplates);

            if (!this.isUpdate && this.defaultReportTemplate) {
              this.checklist.report_template = this.defaultReportTemplate.id;
            }

            if (this.resourceId) {
              this.fetchWordTemplate();
            } else {
              this.loading = false;
            }
          },
          uuidv4(),
          () => {
            const reportTemplates = [exampleDefaultReportTemplate];
            this.$store.commit('checklist_builder/loadReportTemplates', reportTemplates);

            if (!this.isUpdate && this.defaultReportTemplate) {
              this.checklist.report_template = this.defaultReportTemplate.id;
            }

            this.loading = false;
          }
        );
        getRecord.run();
      },

      fetchWordTemplate() {
        const checklistId = parseInt(this.resourceId, 10);
        const getWordTemplate = useTask(
          () => getByChecklist(checklistId),
          response => {
            const wordTemplate = response.parsed;

            if (wordTemplate?.id || this.checklist.wordTemplateId) {
              const fileUrl = wordTemplate?.fileUrl || wordTemplate?.file_url;
              let fileName = 'word_template.docx';
              if (fileUrl) {
                fileName = fileUrl.split('/').pop();
                fileName = decodeURIComponent(fileName);
              }

              let displayName = wordTemplate?.fileName || wordTemplate?.file_name || fileName;
              if (displayName && displayName !== fileName) {
                displayName = decodeURIComponent(displayName);
              }

              this.existingWordTemplate = {
                id: wordTemplate?.id || this.checklist.wordTemplateId,
                name: displayName,
                url: fileUrl,
              };

              if (wordTemplate?.id) {
                this.checklist.wordTemplateId = wordTemplate.id;
              }

              this.$nextTick(() => {
                this.determineReportBuilderType();
              });
            }

            this.loading = false;
          },
          uuidv4(),
          () => {
            this.loading = false;
          }
        );
        getWordTemplate.run();
      },

      determineReportBuilderType() {
        if (this.checklist.report_template) {
          this.reportBuilderType = 'lumiform';
        } else if (this.existingWordTemplate?.id || this.checklist.wordTemplateId) {
          this.reportBuilderType = 'word';
        }
      },
      discard() {
        trackClick(CLICK_EVENTS.DISCARD_REPORT);
        this.$store.commit('checklist_builder/editReportTemplate', null);
      },
      add() {
        trackClick(CLICK_EVENTS.ADD_REPORT_TEMPLATE);
        const temporaryTemplate = JSON.parse(JSON.stringify(this.reportTemplates[0]));
        temporaryTemplate.data.title = this.$t('New Report Template');
        temporaryTemplate.data.isDefault = false;
        temporaryTemplate.data.checklist = null;
        this.$store.commit('checklist_builder/editReportTemplate', temporaryTemplate);
      },
      edit(template) {
        const temporaryTemplate = JSON.parse(JSON.stringify(template));
        this.$store.commit('checklist_builder/editReportTemplate', temporaryTemplate);
      },
      deleteTemplate(template) {
        const getRecord = useTask(
          () => remove(template.id),
          () => {
            const index = this.reportTemplates
              .findIndex(({ id }) => id === template.id);
            this.reportTemplates.splice(index, 1);
          },
        );
        getRecord.run();
      },
      async save(resolveCallback) {
        const resolveSave = result => typeof resolveCallback === 'function' && resolveCallback(result);

        const mutation = response => {
          const savedReportTemplate = response.parsed;

          this.fetch();

          if (!this.editedReportTemplate.id) {
            this.select(savedReportTemplate);
          }

          trackClick(CLICK_EVENTS.SAVE_REPORT);

          this.$store.commit('checklist_builder/editReportTemplate', null);

          resolveSave(true);
        };

        if (this.editedReportTemplate.id) {
          const updateRecord = useTask(
            version => update(this.editedReportTemplate.id, versioned(this.editedReportTemplate.data, version)),
            mutation,
            uuidv4(),
            () => resolveSave(false),
          );

          useTask(
            () => get(this.editedReportTemplate.id),
            ({ version }) => {
              updateRecord.run(version);
            },
            uuidv4(),
            () => resolveSave(false),
          ).run();
        } else {
          const dataToSend = {
            ...this.editedReportTemplate.data,
          };

          const getRecord = useTask(
            () => create(dataToSend),
            mutation,
            uuidv4(),
            () => resolveSave(false),
          );
          getRecord.run();
        }
      },
      select(template) {
        this.checklist.report_template = template.id;
      },

      updateAverageScoreOnChecklist(template) {
        this.$store.commit(
          'checklist_builder/updateAverageWeightedScoreCalculation',
          template?.data?.template?.average_weighted_score_calculation ?? false
        );
      },

      onWordTemplateFileChange(file) {
        this.wordTemplateFile = file;

        if (file) {
          this.saveWordReportTemplate();
        }
      },

      saveWordReportTemplate() {
        this.$store.commit('checklist_builder/setWordTemplateUploading', true);

        if (this.existingWordTemplate?.id) {
          const updateRecord = useTask(
            () => updateWordTemplate(this.existingWordTemplate.id, this.wordTemplateFile),
            () => {
              this.existingWordTemplate = {
                ...this.existingWordTemplate,
                name: this.wordTemplateFile.name,
              };
              trackClick(CLICK_EVENTS.SAVE_REPORT);
              this.$store.commit('checklist_builder/setWordTemplateUploading', false);
            },
            uuidv4(),
            () => {
              console.error('Failed to update word template');
              this.$store.commit('checklist_builder/setWordTemplateUploading', false);
            },
          );
          updateRecord.run();
        } else {
          const uploadRecord = useTask(
            () => uploadWordTemplate(this.wordTemplateFile),
            response => {
              const result = response.parsed;

              this.checklist.wordTemplateId = result.id;

              this.existingWordTemplate = {
                id: result.id,
                name: this.wordTemplateFile.name,
                url: null,
              };

              trackClick(CLICK_EVENTS.SAVE_REPORT);
              this.$store.commit('checklist_builder/setWordTemplateUploading', false);
            },
            uuidv4(),
            () => {
              console.error('Failed to upload word template');
              this.$store.commit('checklist_builder/setWordTemplateUploading', false);
            },
          );
          uploadRecord.run();
        }
      },

      onWordTemplateFileDeleted() {
        if (!this.existingWordTemplate || !this.existingWordTemplate.id) {
          return;
        }

        const templateId = this.existingWordTemplate.id;

        const deleteRecord = useTask(
          () => removeWordTemplate(templateId),
          () => {
            this.existingWordTemplate = null;
            this.checklist.wordTemplateId = null;
          },
          uuidv4(),
          () => {
            console.error('Failed to delete word template');
          },
        );
        deleteRecord.run();
      },

      onWordTemplateUploadingChange(isUploading) {
        this.$store.commit('checklist_builder/setWordTemplateUploading', isUploading);
      },
    },
  };
</script>
